import React, { useContext, useCallback, Fragment } from 'react';
import {
  Collapsible,
  HighlightGroup,
  InputPlaceholder,
  Subtext,
  Row,
  Form
} from '@contently/toolkit';
import ReactPlaceholder from 'react-placeholder';
import CheckboxSection from '../forms/checkboxes/checkbox_section';
import { FormikHandlersContext } from '../forms/dynamic_forms/DynamicFormikForm';
import useDeepCompareMemo from '../../utils/useDeepCompareMemo';

const DEFAULT_TYPE = 'default';
const PERFORMANCE_TYPE = 'performance';
const DOCALYTICS_TYPE = 'docalytics';
const DOCALYTICS_CUSTOM_TYPE = 'docalytics_custom_domain';
const PROPERTY_TYPES = [
  { type: PERFORMANCE_TYPE, label: 'Content from my websites' },
  { type: DEFAULT_TYPE, label: 'Other content published by these publications' },
  { type: DOCALYTICS_CUSTOM_TYPE, label: 'Docalytics documents from my custom URLs' },
  {
    type: DOCALYTICS_TYPE,
    label: 'Docalytics documents published by these publications'
  }
];

function PropertiesCheckboxes(props) {
  const {
    accountId,
    onFetching,
    onError,
    fetching,
    value,
    errors,
    touched,
    required,
    renderSubtext,
    id,
    properties,
    label
  } = props;

  const { setFieldValue, isFetching } = useContext(FormikHandlersContext);

  const compareProperties = (a, b) => {
    const labelA = a.label.toUpperCase();
    const labelB = b.label.toUpperCase();

    let comparison = 0;
    if (labelA > labelB) {
      comparison = 1;
    } else if (labelA < labelB) {
      comparison = -1;
    }
    return comparison;
  };

  const handleChange = useCallback(
    data => {
      // data is from one of multiple checkbox groups
      //   so make sure to add any checked checkboxes, remove any unchecked
      //   without mutating other value data
      let vals = [...value];
      const toAdd = data.filter(opt => opt.checked);
      const toRemove = data.filter(opt => !opt.checked);

      // Add checked
      toAdd.forEach(opt => {
        if (!vals.includes(opt.value)) {
          vals.push(opt.value);
        }
      });

      // Remove unchecked
      vals = vals.filter(v => !toRemove.map(opt => opt.value).includes(v));

      setFieldValue(id, vals);
    },
    [value, id, setFieldValue]
  );

  const subtext = () => {
    if (renderSubtext && typeof renderSubtext === 'function') {
      return renderSubtext();
    }

    if (renderSubtext && typeof renderSubtext === 'string') {
      return <Subtext>{renderSubtext}</Subtext>;
    }

    return null;
  };

  const sections = useDeepCompareMemo(() => {
    return PROPERTY_TYPES.map(propertyType => {
      const formattedOptions = properties
        .filter(p => p.type === propertyType.type)
        .map(opt => {
          return {
            ...opt,
            checked: value.includes(opt.value),
            key: opt.value,
            label:
              opt.type === PERFORMANCE_TYPE || opt.type === DOCALYTICS_CUSTOM_TYPE
                ? opt.siteUrl
                : opt.brandProfileName
          };
        })
        .sort(compareProperties);

      if (!formattedOptions.length) {
        return <Fragment key={propertyType.type} />;
      }

      return (
        <Row key={propertyType.type}>
          <Collapsible header={propertyType.label}>
            <CheckboxSection
              onChange={handleChange}
              options={formattedOptions}
              id={`${propertyType.type}Properties`}
              noTranslate
            />
          </Collapsible>
        </Row>
      );
    });
  }, [properties, value]);

  return (
    <Row>
      <ReactPlaceholder
        showLoadingAnimation
        customPlaceholder={<InputPlaceholder wrapperClass="row" withLabel />}
        ready={!isFetching}
      >
        <Form.Label>{label}</Form.Label>
        {subtext()}
        <HighlightGroup>{sections}</HighlightGroup>
      </ReactPlaceholder>
    </Row>
  );
}

export default PropertiesCheckboxes;



// WEBPACK FOOTER //
// ./app/javascript/app/components/integrations/PropertiesCheckboxes.jsx